"use client";

/**
 * REMOVAL NOTE — this feature is intentionally self-contained so it can be
 * swapped for App Store smart banners once the native iOS app ships. To
 * remove it entirely: delete this file, `src/lib/install-prompt.ts` (+ its
 * test), the `install` namespace in `messages/en.json` / `messages/tr.json`,
 * and the single `<InstallPrompt />` mount in `src/components/shell/AppShell.tsx`.
 *
 * Polite PWA install prompt for logged-in surfaces (AppShell guarantees
 * auth). Slides a small glass card up ~8s after mount when the browser can
 * actually install: Android/desktop Chrome uses the captured
 * `beforeinstallprompt` event; iOS Safari gets a bottom sheet with the
 * Share → Add to Home Screen steps. Dismissal snoozes for 30 days, an
 * accepted install silences it forever, and being shown once (even ignored)
 * ends the attempt for the session — see {@link shouldShowInstallPrompt}.
 *
 * Prompt coordination: bottom-anchored soft prompts mark themselves with a
 * `data-prompt` attribute (see PushPrompt). Before revealing, this component
 * checks for any `[data-prompt]` element already in the DOM and, if one
 * exists, yields for the whole session — so it never stacks on the push
 * prompt on /matches.
 */

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button, GlassCard, Modal } from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  INSTALL_PROMPT_DELAY_MS,
  INSTALL_SHOWN_SESSION_KEY,
  INSTALL_SNOOZE_FOREVER,
  INSTALL_SNOOZE_KEY,
  dismissSnoozeUntil,
  getInstallPath,
  isIosUserAgent,
  parseSnooze,
  shouldShowInstallPrompt,
  type InstallPath,
  type InstallPromptEnv,
} from "@/lib/install-prompt";

/** Chromium's non-standard install event (not in lib.dom). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function writeSnooze(until: number) {
  try {
    window.localStorage.setItem(INSTALL_SNOOZE_KEY, String(until));
  } catch {
    /* ignore storage failures */
  }
}

function markShownThisSession() {
  try {
    window.sessionStorage.setItem(INSTALL_SHOWN_SESSION_KEY, "1");
  } catch {
    /* ignore storage failures */
  }
}

/** Snapshot the browser environment for the pure visibility predicate. */
function collectEnv(hasInstallEvent: boolean): InstallPromptEnv {
  let snoozeUntil: number | null = null;
  let shownThisSession = false;
  try {
    snoozeUntil = parseSnooze(window.localStorage.getItem(INSTALL_SNOOZE_KEY));
    shownThisSession = window.sessionStorage.getItem(INSTALL_SHOWN_SESSION_KEY) !== null;
  } catch {
    /* storage blocked → treat as fresh */
  }
  return {
    standalone:
      window.matchMedia("(display-mode: standalone)").matches ||
      ("standalone" in window.navigator &&
        (window.navigator as { standalone?: boolean }).standalone === true),
    snoozeUntil,
    shownThisSession,
    hasInstallEvent,
    supportsInstallEvent: "onbeforeinstallprompt" in window,
    iosUserAgent: isIosUserAgent(window.navigator.userAgent, window.navigator.maxTouchPoints),
    now: Date.now(),
  };
}

export function InstallPrompt() {
  const t = useTranslations("install");
  const [path, setPath] = useState<InstallPath | null>(null);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  // The captured beforeinstallprompt event; a ref so the reveal timer always
  // sees the latest capture without re-arming.
  const installEventRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      installEventRef.current = event as BeforeInstallPromptEvent;
    };
    const onAppInstalled = () => {
      writeSnooze(INSTALL_SNOOZE_FOREVER);
      setPath(null);
      setIosSheetOpen(false);
    };
    const reveal = () => {
      const env = collectEnv(installEventRef.current !== null);
      if (!shouldShowInstallPrompt(env)) return;
      markShownThisSession();
      // Another soft prompt (e.g. PushPrompt on /matches) is on screen —
      // yield quietly; the session flag keeps us from retrying elsewhere.
      if (document.querySelector("[data-prompt]")) return;
      setPath(getInstallPath(env));
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    const timer = window.setTimeout(reveal, INSTALL_PROMPT_DELAY_MS);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      window.clearTimeout(timer);
    };
  }, []);

  if (path === null) return null;

  function snoozeAndHide() {
    writeSnooze(dismissSnoozeUntil(Date.now()));
    setIosSheetOpen(false);
    setPath(null);
  }

  async function installNative() {
    const event = installEventRef.current;
    if (!event) return;
    setPath(null);
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      writeSnooze(outcome === "accepted" ? INSTALL_SNOOZE_FOREVER : dismissSnoozeUntil(Date.now()));
    } catch {
      // prompt() throws if already used — treat as a dismissal.
      writeSnooze(dismissSnoozeUntil(Date.now()));
    } finally {
      installEventRef.current = null;
    }
  }

  return (
    <>
      <div
        data-prompt="install"
        className={cn(
          "pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4",
          // clears the fixed BottomTabBar (z-40) on mobile; sits in the
          // bottom-right corner on desktop where there is no tab bar
          "bottom-[calc(5rem+env(safe-area-inset-bottom))]",
          "md:inset-x-auto md:right-6 md:bottom-6 md:px-0",
        )}
      >
        <GlassCard
          padding="sm"
          className="pointer-events-auto w-full max-w-sm animate-rise bg-elevated/90 shadow-lift"
        >
          <div className="flex items-start gap-3">
            <Image
              src="/brand/icon-192.png"
              alt=""
              width={44}
              height={44}
              className="mt-0.5 size-11 shrink-0 rounded-xl border border-white/10"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{t("title")}</p>
              <p className="mt-0.5 text-xs leading-snug text-white/60">{t("body")}</p>
              <Button
                size="sm"
                className="mt-3"
                onClick={path === "native" ? installNative : () => setIosSheetOpen(true)}
              >
                {path === "native" ? t("install") : t("howTo")}
              </Button>
            </div>
            <button
              type="button"
              onClick={snoozeAndHide}
              aria-label={t("dismiss")}
              className="-mt-1 -mr-1 flex size-7 shrink-0 items-center justify-center rounded-full text-white/45 transition-colors hover:bg-white/8 hover:text-white"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </GlassCard>
      </div>

      {/* iOS instructions: viewing them counts as an answer → 30-day snooze. */}
      <Modal open={iosSheetOpen} onClose={snoozeAndHide} size="sm" title={t("ios.title")}>
        <ol className="space-y-4">
          <li className="flex items-center gap-3.5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-flamingo-light">
              {/* iOS share glyph */}
              <svg
                viewBox="0 0 24 24"
                className="size-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v12" />
                <path d="M8 6.5L12 3l4 3.5" />
                <path d="M8 10H6.5A2.5 2.5 0 0 0 4 12.5v6A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5v-6A2.5 2.5 0 0 0 17.5 10H16" />
              </svg>
            </span>
            <p className="text-sm leading-snug text-white/80">{t("ios.step1")}</p>
          </li>
          <li className="flex items-center gap-3.5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-flamingo-light">
              {/* "Add to Home Screen" glyph */}
              <svg
                viewBox="0 0 24 24"
                className="size-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="4" y="4" width="16" height="16" rx="4.5" />
                <path d="M12 9v6M9 12h6" />
              </svg>
            </span>
            <p className="text-sm leading-snug text-white/80">{t("ios.step2")}</p>
          </li>
        </ol>
        <p className="mt-5 text-xs leading-snug text-white/55">{t("ios.note")}</p>
        <Button className="mt-5 w-full" onClick={snoozeAndHide}>
          {t("ios.done")}
        </Button>
      </Modal>
    </>
  );
}
